import { type LWApiPublicAcronisBackupUsagesResultRawI } from '@liquidweb-dev/club-sauce/public/acronis/backup/index.raw';
import {
	LWApiPublicProductDetailsResultOptionRawI,
	type LWApiPublicProductFlatDetailsResultFlatOptionValueRawI,
	type LWApiPublicProductFlatDetailsResultRawI,
} from '@liquidweb-dev/club-sauce/public/product/index.raw';

import { type AssetDetails } from 'modules/queries/asset/useDetails';
import formatBytes from 'utility/formatBytes';
import { findOption, findOptionValue } from 'utility/products/options';
import { getQuotaPricePath } from 'utility/redux/selectorHelperFunctions/backups';

export interface BackupOption {
	value: string;
	label: string;
	monthlyPrice?: number;
}

const backupsDisplayMap: {
	None: string;
	CloudDaily: string;
	Daily: string;
	Quota: string;
	Acronis: string;
} = {
	None: 'None',
	CloudDaily: 'Pay per GB',
	Daily: 'Pay per GB',
	Quota: 'Storage quota',
	Acronis: 'Acronis Backups',
};

/**
 * Returns the backup type for a given backup plan.
 * @param backupPlan The backup plan value.
 */
export const getType = (
	backupPlan?: string,
): 'Daily' | 'CloudDaily' | 'Quota' | 'None' => {
	switch (backupPlan) {
		case 'BackupDay':
		case 'Daily':
			return 'Daily';
		case 'CloudDaily':
			return 'CloudDaily';
		case 'BackupQuota':
		case 'Quota':
			return 'Quota';
		default:
			return 'None';
	}
};

/** Acronis Cloud destinations are `AcronisCloudStorage` or `AcronisCloudStorage.*`; the rest are brand storage. */
const ACRONIS_CLOUD_DESTINATION_PREFIX = 'AcronisCloudStorage';

export const isAcronisCloudDestination = (value?: string) =>
	value === ACRONIS_CLOUD_DESTINATION_PREFIX ||
	Boolean(value?.startsWith(`${ACRONIS_CLOUD_DESTINATION_PREFIX}.`));

/** Sorts by `display_order`, unordered last (not `Infinity`: `Infinity - Infinity` is `NaN`). */
const compareDisplayOrder = (
	a: { display_order?: number },
	b: { display_order?: number },
) =>
	(a.display_order ?? Number.MAX_SAFE_INTEGER) -
	(b.display_order ?? Number.MAX_SAFE_INTEGER);

/**
 * Public quotas priced in the asset's region, in display order — the list `QuotaSelect` renders.
 * `retainPtovId` keeps the server's current quota listed even when the filter drops it (e.g. grandfathered plans).
 */
export const getAcronisQuotaOptions = (
	values: LWApiPublicProductFlatDetailsResultFlatOptionValueRawI[] | undefined,
	regionId: number,
	retainPtovId?: number,
) =>
	values
		?.filter(
			(item) =>
				(item.public && regionId in (item?.region_id_prices || {})) ||
				(retainPtovId !== undefined && item.ptov_id === retainPtovId),
		)
		?.sort(compareDisplayOrder) || [];

/** Acronis Cloud destinations first, then by `display_order`. */
export const compareAcronisDestinations = (
	a: { display_order?: number; value?: string },
	b: { display_order?: number; value?: string },
) => {
	const aIsCloud = isAcronisCloudDestination(a.value);
	const bIsCloud = isAcronisCloudDestination(b.value);

	if (aIsCloud !== bIsCloud) return aIsCloud ? -1 : 1;

	return compareDisplayOrder(a, b);
};

/**
 * Whether a server currently has a backup plan configured.
 *
 * `supplementDetails` builds `backupPlan` and `backupsState` together and
 * derives `hasBackups` from that same plan value, so this reads the derived
 * flag alone — a partially-populated shape carrying one without the other
 * never reaches callers. Branch on this rather than re-deriving it, so the
 * page-level layout and the Backups block can never disagree about which
 * state is on screen.
 */
export const getHasBackups = (assetDetails?: AssetDetails): boolean =>
	assetDetails?.backupsState?.hasBackups ?? false;

const getOptions = (
	assetDetails: AssetDetails,
	backupPlans: LWApiPublicProductDetailsResultOptionRawI['values'],
) => {
	const OPTIONS_ORDER = [
		'None',
		'AcronisBackup',
		'BackupDay',
		'CloudDaily',
		'Daily',
		'Quota',
	];

	const {
		region_id: regionId,
		backupsState,
		featureDetails,
	} = assetDetails || {};
	const currentPlan = featureDetails?.LiquidWebBackupPlan;

	const options: BackupOption[] = [
		{ value: 'None', label: backupsDisplayMap.None },
	];

	const isOptionAlreadyIncluded = (value: string) =>
		options.some((option) => option.value === value);

	if (backupsState?.canAcronis || backupsState?.hasAcronis) {
		options.push({
			value: 'AcronisBackup',
			label: backupsDisplayMap.Acronis,
		});
	}

	if (currentPlan && !isOptionAlreadyIncluded(currentPlan.value)) {
		const [, backupsInfo] =
			Object.entries(featureDetails).find(([key, value]) => {
				return (
					key === getQuotaPricePath(currentPlan.value) &&
					value.parent_ptov_id === currentPlan.ptov_id
				);
			}) || [];

		options.push({
			value: currentPlan.value,
			label:
				backupsDisplayMap[currentPlan.value as keyof typeof backupsDisplayMap],
			monthlyPrice: parseFloat(backupsInfo?.monthly_price || '0') || 0,
		});
	}

	if (backupsState?.canCloudBackup) {
		backupPlans?.forEach((plan) => {
			if (!isOptionAlreadyIncluded(plan.value)) {
				if (
					getType(plan.value) !== 'Daily' &&
					getType(plan.value) !== 'CloudDaily'
				) {
					options.push({
						value: plan.value,
						label:
							backupsDisplayMap[plan.value as keyof typeof backupsDisplayMap],
						monthlyPrice: 0,
					});
				} else {
					const path = getQuotaPricePath(plan.value);
					const daily =
						findOptionValue('Daily', backupPlans)?.options ||
						findOptionValue('CloudDaily', backupPlans)?.options;
					const option = findOption(path, daily);
					// As per backend team, the key for the quota option is always 1GBHR
					const LW_BACKUP_PLAN_QUOTA_OPTIONS_LOOKUP_KEY = '1GBHR';
					const price = option?.values?.find(
						(valueItem: { value: string }) =>
							valueItem.value === LW_BACKUP_PLAN_QUOTA_OPTIONS_LOOKUP_KEY,
					)?.prices[regionId]?.month;

					if (price) {
						options.push({
							value: plan.value,
							label:
								backupsDisplayMap[plan.value as keyof typeof backupsDisplayMap],
							monthlyPrice: price,
						});
					}
				}
			}
		});
	}

	options.sort(
		(a, b) => OPTIONS_ORDER.indexOf(a.value) - OPTIONS_ORDER.indexOf(b.value),
	);

	return options;
};

export const getCloudOptions = (
	assetDetails: AssetDetails,
	backupPlans: LWApiPublicProductDetailsResultOptionRawI['values'],
) => {
	return getOptions(assetDetails, backupPlans).filter(
		(opt) => opt.value !== 'AcronisBackup' && opt.value !== 'None',
	);
};

/**
 * Calculate the monthly backup cost based on asset details and product details
 * @returns Undefined if missing data. Otherwise returns the monthly backup cost
 */
export const getMonthlyAcronisBackupCost = (
	/** Whether the asset has Acronis backups */
	hasAcronis: boolean,
	/** The region ID for pricing */
	regionId?: number,
	/** Acronis product details with pricing information */
	acronisProductDetails?: LWApiPublicProductFlatDetailsResultRawI,
	/** Acronis asset details */
	acronisAssetDetails?: AssetDetails,
): number | undefined => {
	if (
		!hasAcronis ||
		!acronisProductDetails ||
		!acronisAssetDetails ||
		!regionId
	) {
		return undefined;
	}

	const { featureDetails } = acronisAssetDetails;
	const { region_id_prices: acronisPrices } = acronisProductDetails;

	if (!featureDetails || !acronisPrices) return undefined;

	const { AcronisStorageQuotaCloud, AcronisStorageQuotaLiquidWeb } =
		featureDetails;

	const quota = AcronisStorageQuotaCloud || AcronisStorageQuotaLiquidWeb;

	const { price } = quota;
	const acronisBasePrice = acronisPrices[String(regionId)];

	const total = Number(acronisBasePrice) + Number(price);

	if (Number.isNaN(total)) return undefined;
	return total;
};

/**
 * Calculate the monthly backup cost based on asset details and product details
 * @returns Undefined if missing data. Otherwise returns the monthly backup cost
 */
export const getMonthlyBackupCost = (
	/** The parent asset's details */
	assetDetails: AssetDetails | undefined,
	/** The Acronis product's details */
	acronisProductDetails?: LWApiPublicProductFlatDetailsResultRawI,
	/** The Acronis asset's details */
	acronisAssetDetails?: AssetDetails,
): number | undefined => {
	if (!assetDetails) return undefined;
	const { hasAcronis, region_id: regionId } = assetDetails;
	if (hasAcronis) {
		return getMonthlyAcronisBackupCost(
			hasAcronis,
			regionId,
			acronisProductDetails,
			acronisAssetDetails,
		);
	}

	const { featureDetails } = assetDetails;

	// Quota plans have a fixed monthly price
	if (assetDetails.backupPlan === 'Quota') {
		const monthlyPrice = featureDetails?.BackupQuota?.monthly_price;
		if (Number.isNaN(monthlyPrice)) return undefined;
		return Number(monthlyPrice);
	}

	if (!featureDetails || !featureDetails.BackupStorage) return undefined;
	const {
		BackupStorage: { monthly_price: monthlyPrice, price, cycle },
	} = featureDetails;

	const backupSize = parseFloat(assetDetails.instance?.backup_size || '0');
	let monthlyPricePerGB = monthlyPrice;

	if (!monthlyPricePerGB && cycle === 'hourly') {
		// Convert hourly price to monthly price (assuming 24*30.4 hours per month as we had before: https://git.liquidweb.com/techdev/wallaby/-/blob/d97cb742ad435d4d96841a220c7e49165bf89c7c/src/modules/api/asset/detailsModule/index.js#L892)
		monthlyPricePerGB = String(Number(price) * 24 * 30.4);
	}
	if (!monthlyPricePerGB) return undefined;

	// Otherwise multiply by the backup size
	return Number(monthlyPricePerGB) * backupSize;
};

export const getTotalAcronisBackupUsed = (
	acronisUsages: LWApiPublicAcronisBackupUsagesResultRawI,
) => acronisUsages.usages.storage?.value || 0;

export const getTotalAcronisBackupQuota = (
	acronisUsages: LWApiPublicAcronisBackupUsagesResultRawI,
) => acronisUsages.usages.storage?.quota?.max_usage || 0;
/**
 * The bytes an Acronis subscription has used, or `undefined` when the backup
 * usage source reports no storage figure.
 *
 * `undefined` and `0` are distinct: a missing figure is not the same as a
 * genuine zero.
 */
export const getAcronisStorageUsed = (
	acronisUsages?: LWApiPublicAcronisBackupUsagesResultRawI,
): number | undefined => acronisUsages?.usages.storage?.value;

/**
 * The Acronis storage usage ratio, or `undefined` unless both a usage figure
 * and a quota to measure it against are reported.
 *
 * A quota of zero counts as unreported: it is not a usable denominator, and a
 * zero total means the usage source is wrong rather than that the
 * subscription is full.
 */
export const getAcronisStorageUsage = (
	acronisUsages?: LWApiPublicAcronisBackupUsagesResultRawI,
): { used: number; quota: number } | undefined => {
	const used = getAcronisStorageUsed(acronisUsages);
	const quota = acronisUsages?.usages.storage?.quota?.max_usage;

	if (used === undefined || quota === undefined || quota <= 0) {
		return undefined;
	}

	return { used, quota };
};

/**
 * Get the total backup size with unit
 */
export const getTotalBackupSize = (
	/** The asset details object */
	assetDetails: AssetDetails | undefined,
	/** Acronis usage data */
	acronisUsages?: LWApiPublicAcronisBackupUsagesResultRawI,
) => {
	if (!assetDetails) return undefined;

	const { hasAcronis } = assetDetails;
	const acronisStorageUsed = getAcronisStorageUsed(acronisUsages);
	if (hasAcronis && acronisStorageUsed !== undefined) {
		return formatBytes(acronisStorageUsed).withUnit;
	}

	if (assetDetails.instance?.backup_size) {
		return `${assetDetails.instance.backup_size} GB`;
	}

	return undefined;
};
